import React, { useState } from "react";
import { Button } from "./ui/button";
import { Phone, Menu, X, LogIn, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { trackPhoneClick } from "../utils/analytics";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // L'espace client n'existe pas encore. Plutôt qu'un lien mort ou un bouton
  // grisé sans explication, le bouton reste actif et dit pourquoi il ne mène
  // nulle part.
  const [connexionAnnoncee, setConnexionAnnoncee] = useState(false);
  const MESSAGE_CONNEXION =
    "L'espace client n'est pas encore ouvert. Nous vous préviendrons dès sa mise en service.";

  return (
    <>
      {/* Lien d'évitement : invisible à la souris, premier arrêt de tabulation
          au clavier. Il permet de sauter la navigation, qui est identique sur
          toutes les pages. */}
      <a
        href="#contenu"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-orange-700 focus:px-4 focus:py-3 focus:text-white focus:shadow-lg"
      >
        Aller au contenu principal
      </a>

      <header className="bg-white shadow-sm border-b sticky top-0 z-50">

        {/* Barre utilitaire : téléphone à gauche, accès professionnels à
            droite. Masquée sous 640 px, où ces entrées sont reprises dans le
            menu latéral. */}
        <div className="hidden sm:block border-b border-gray-100 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex items-center justify-between gap-4 py-1.5">
              <a
                href="tel:0939035444"
                onClick={() => trackPhoneClick("entete-bureau")}
                className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-orange-700 transition-colors"
              >
                <Phone aria-hidden="true" className="h-4 w-4" />
                09 39 03 54 44
              </a>

              <div className="flex items-center gap-2">
                <Link to="/acces-pro">
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-2 border-green-700 bg-white text-green-700 hover:bg-green-50 hover:text-green-800"
                  >
                    Accès Pro
                  </Button>
                </Link>

                {/* relative : le message se pose sous le bouton sans pousser
                    la barre, qui est en position collante. */}
                <div className="relative">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setConnexionAnnoncee(!connexionAnnoncee)}
                    aria-expanded={connexionAnnoncee}
                    className="border-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <LogIn aria-hidden="true" className="h-4 w-4" />
                    Connexion
                  </Button>
                  {connexionAnnoncee && (
                    <p
                      role="status"
                      className="absolute right-0 top-full z-50 mt-2 w-64 rounded-md border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 shadow-lg"
                    >
                      {MESSAGE_CONNEXION}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-4 py-3">
          
          {/* MOBILE + TABLETTE : <= 1024px */}
          <div className="flex items-center justify-between lg:hidden">
            {/* Hamburger */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-3 rounded-md text-gray-700 hover:text-orange-700"
              aria-label="Ouvrir le menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Menu aria-hidden="true" className="h-6 w-6" />
            </button>

            {/* Logo centré mobile/tablette */}
            <Link to="/" className="flex flex-col items-center">
              <img 
                src="/images/logo-tef.jpg"
                width="184"
                height="80"
                loading="eager" 
                alt="Transition Énergie France" 
                className="w-20 h-14 sm:w-28 sm:h-20 md:w-36 md:h-24 object-contain"
              />
              <p className="text-xs sm:text-sm font-medium text-center">
                <span className="text-blue-600 font-bold">AMO</span>
                <span className="text-orange-700"> Ma Prime Adapt • IDF</span>
              </p>
            </Link>
            
            {/* Appeler + Simulateur.

                Le numéro vivait dans la barre utilitaire, masquée sous 640 px,
                et dans le menu latéral : sur un téléphone il fallait donc
                ouvrir un menu pour le trouver. Or 44 % des visiteurs viennent
                d'un mobile, et le public visé — personnes âgées et leurs
                aidants — appelle plus volontiers qu'il ne remplit un
                formulaire. Le libellé disparaît sous 640 px, l'icône seule
                tenant dans 44 px de côté, le minimum exigé pour une cible
                tactile. */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              <a
                href="tel:0939035444"
                onClick={() => trackPhoneClick("entete-mobile")}
                aria-label="Appeler le 09 39 03 54 44"
                className="flex items-center justify-center gap-1.5 min-h-[44px] min-w-[44px] px-2 sm:px-3 rounded-md border-2 border-green-700 text-green-700 text-xs sm:text-sm font-medium hover:bg-green-50 transition-colors"
              >
                <Phone aria-hidden="true" className="h-5 w-5" />
                <span className="hidden sm:inline">Appeler</span>
              </a>

              <Link to="/simulateur">
                <Button className="bg-orange-700 hover:bg-orange-800 text-white text-xs sm:text-sm px-3 sm:px-4 min-h-[44px]">
                  Simulateur
                </Button>
              </Link>
            </div>
          </div>

          {/* DESKTOP : >= 1024px */}
          <div className="hidden lg:flex items-center justify-between">
            
            {/* Logo desktop à gauche */}
            <Link to="/" className="flex flex-col items-center">
              <img 
                src="/images/logo-tef.jpg"
                width="184"
                height="80"
                loading="eager" 
                alt="Transition Énergie France" 
                className="w-48 h-32 xl:w-56 xl:h-36 object-contain"
              />
              <p className="text-sm xl:text-base font-medium text-center">
                <span className="text-blue-600 font-bold">AMO</span>
                <span className="text-orange-700"> Ma Prime Adapt • Île-de-France</span>
              </p>
            </Link>
            
            {/* Menu desktop centré */}
            <nav className="flex items-center space-x-6 xl:space-x-8">
              <Link to="/" className="text-gray-700 hover:text-orange-700 font-medium transition-colors text-sm xl:text-base">
                Accueil
              </Link>
              <Link to="/salle-de-bain" className="text-gray-700 hover:text-orange-700 font-medium transition-colors text-sm xl:text-base">
                Salle de bain
              </Link>
              <Link to="/accessibilite" className="text-gray-700 hover:text-orange-700 font-medium transition-colors text-sm xl:text-base">
                Accessibilité
              </Link>
              <Link to="/exterieurs" className="text-gray-700 hover:text-orange-700 font-medium transition-colors text-sm xl:text-base">
                Extérieurs
              </Link>
              <div className="relative group">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 text-gray-700 hover:text-orange-700 font-medium transition-colors text-sm xl:text-base"
                  aria-haspopup="true"
                >
                  Aides
                  <ChevronDown className="h-4 w-4" aria-hidden="true" />
                </button>
                <ul className="invisible absolute left-0 top-full z-50 mt-1 w-60 rounded-md border border-gray-200 bg-white py-1 opacity-0 shadow-lg transition group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                  <li>
                    <Link to="/maprimeadapt" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700">
                      MaPrimeAdapt'
                    </Link>
                  </li>
                  <li>
                    <Link to="/aides-complementaires" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-700">
                      Aides complémentaires
                    </Link>
                  </li>
                </ul>
              </div>
              <Link to="/partenaires" className="text-gray-700 hover:text-orange-700 font-medium transition-colors text-sm xl:text-base">
                Partenaires
              </Link>
              {/* Le menu mobile lie /contact, mais il n'est rendu qu'une fois
                  ouvert : le lien n'existait dans aucune page prérendue. */}
              <Link to="/contact" className="text-gray-700 hover:text-orange-700 font-medium transition-colors text-sm xl:text-base">
                Contact
              </Link>
            </nav>

            {/* Boutons desktop à droite */}
            <div className="flex items-center space-x-4">
              <Link to="/simulateur">
                <Button className="bg-orange-700 hover:bg-orange-800 text-white shadow-lg text-sm xl:text-base">
                  Tester mon éligibilité
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar mobile/tablette */}
      {isMobileMenuOpen && (
        <>
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          
          <div className="fixed top-0 left-0 w-80 h-full bg-white shadow-lg z-50 lg:hidden">
            <div className="p-4">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Menu</h3>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-3 rounded-md text-gray-700 hover:text-orange-700"
                  aria-label="Fermer le menu"
                >
                  <X aria-hidden="true" className="h-6 w-6" />
                </button>
              </div>
              
              <nav className="space-y-1">
                <Link 
                  to="/" 
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-md font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  🏠 Accueil
                </Link>
                <Link 
                  to="/salle-de-bain" 
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-md font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  🚿 Salle de bain
                </Link>
                <Link 
                  to="/accessibilite" 
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-md font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  ♿ Accessibilité
                </Link>
                <Link 
                  to="/exterieurs" 
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-md font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  🌿 Extérieurs
                </Link>
                <Link 
                  to="/maprimeadapt" 
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-md font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  💰 MaPrimeAdapt'
                </Link>
                <Link
                  to="/aides-complementaires"
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-md font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  💶 Aides complémentaires
                </Link>
                <Link
                  to="/partenaires"
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-md font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  🤝 Partenaires
                </Link>
                <Link 
                  to="/acces-pro" 
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-md font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  🔧 Accès Pro
                </Link>
                <div>
                  <button
                    type="button"
                    onClick={() => setConnexionAnnoncee(!connexionAnnoncee)}
                    aria-expanded={connexionAnnoncee}
                    className="w-full text-left px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-md font-medium"
                  >
                    🔑 Connexion
                  </button>
                  {connexionAnnoncee && (
                    <p role="status" className="px-4 pb-3 text-xs text-gray-600">
                      {MESSAGE_CONNEXION}
                    </p>
                  )}
                </div>
                <Link
                  to="/simulateur"
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-md font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  🧮 Simulateur d'éligibilité
                </Link>
                <Link
                  to="/avis-maprimeadapt"
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-md font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  ⭐ Avis MaPrimeAdapt'
                </Link>
                <Link
                  to="/contact"
                  className="block px-4 py-3 text-gray-700 hover:bg-orange-50 hover:text-orange-700 rounded-md font-medium"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  ✉️ Contact
                </Link>

                <div className="pt-4 mt-4 border-t border-gray-300">
                  <div className="px-4 py-3 bg-blue-50 rounded-md">
                    <div className="flex items-center space-x-2 text-blue-700">
                      <Phone aria-hidden="true" className="h-4 w-4" />
                      <span className="font-medium">09 39 03 54 44</span>
                    </div>
                    <p className="text-xs text-blue-600 mt-1">Appel gratuit</p>
                  </div>
                </div>
              </nav>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Header;